import React, { useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import dreamBoothIcon from '/assets/images/dreambooth-white.png'
import klakklikklukIcon from '/assets/klakklikkluk/logo.png'
import { useAuthStore } from '../stores/authStore'
import { LanguageSelector } from '@renderer/components'
import { capabilities } from '@renderer/lib/runtime'
import { GoogleOAuthCancelled } from '@renderer/lib/auth/googleOAuthHandoff'

const Register: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string>()
  const [googleWaiting, setGoogleWaiting] = useState(false)
  const googleAbort = useRef<AbortController | null>(null)
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle)
  const initializeCookie = useAuthStore((state) => state.initializeCookie)
  const isKlakklikkluk = window.location.hostname.includes('klakklikkluk')

  const handleGoogleSignUp = async (): Promise<void> => {
    const controller = new AbortController()
    googleAbort.current = controller

    try {
      await loginWithGoogle('register', {
        signal: controller.signal,
        onWaiting: () => setGoogleWaiting(true)
      })
      // Initialize cookie in session after OAuth registration
      await initializeCookie()
      // Small delay to ensure cookie is set before navigation
      await new Promise((resolve) => setTimeout(resolve, 100))
      cogoToast.success(
        <span>{t('register.successMessage', 'Account created successfully!')}</span>
      )
      navigate('/')
    } catch (error: unknown) {
      if (error instanceof GoogleOAuthCancelled) return
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('register.networkError', 'Network error. Please try again later.')
      cogoToast.error(<span>{errorMessage}</span>)
      setError(errorMessage)
    } finally {
      setGoogleWaiting(false)
      googleAbort.current = null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-black/90 to-black/70 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <img
          src={isKlakklikkluk ? klakklikklukIcon : dreamBoothIcon}
          alt={t('register.logoAlt', 'DreamBooth Logo')}
          width={200}
          height={50}
          className="mx-auto mb-4"
        />
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-whiteout to-gray-400">
            {t('register.welcome', 'Create Account')}
          </h2>
          <p className="text-gray-400 mt-2">
            {t('register.signUpPrompt', 'Sign up to get started')}
          </p>
        </div>

        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 shadow-xl">
          <div className="space-y-6">
            {error && <p className="text-red-500 text-center">{error}</p>}

            {capabilities.googleOAuth ? (
              <>
                {googleWaiting && (
                  // Same handoff as Login: the operator is in the system browser
                  // and has to walk back here on their own.
                  <div className="rounded-lg border border-gray-600 bg-white/5 p-4 text-center space-y-3">
                    <p className="text-whiteout font-medium">
                      {t('register.googleWaitingTitle', 'Finish signing up with Google')}
                    </p>
                    <p className="text-sm text-gray-400">
                      {t(
                        'register.googleWaitingBody',
                        'Your browser has opened. Sign in there, then return to this app — it will continue on its own.'
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => googleAbort.current?.abort()}
                      className="text-sm text-purpleSnail hover:text-whiteout transition-colors"
                      style={{ touchAction: 'manipulation' }}
                    >
                      {t('register.googleWaitingCancel', 'Cancel')}
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={googleWaiting}
                  style={{ touchAction: 'manipulation' }}
                  className="w-full py-3 font-semibold text-white rounded-lg bg-white/10 border border-gray-600 hover:bg-white/20 transition duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t('register.signUpWithGoogle', 'Sign up with Google')}
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 text-gray-400">{t('register.or', 'OR')}</span>
                  </div>
                </div>

                <p className="text-center text-gray-400 text-sm">
                  {t('register.alreadyHaveAccount', 'Already have an account?')}{' '}
                  <Link
                    to="/login"
                    className="text-purpleSnail hover:text-whiteout transition-colors"
                  >
                    {t('register.signIn', 'Sign in')}
                  </Link>
                </p>

                <p className="text-center text-gray-400 text-xs mt-4">
                  {t(
                    'register.webDashboardPrompt',
                    'Need to create an account with email/password?'
                  )}
                  <br />
                  <a
                    href={`${isKlakklikkluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/register`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purpleSnail hover:text-whiteout transition-colors"
                  >
                    {t('register.visitWebDashboard', 'Visit the web dashboard')}
                  </a>
                </p>
              </>
            ) : (
              <div className="text-center">
                <p className="text-gray-400 mb-4">
                  {t('register.webOnly', 'Registration is only available in the Electron app.')}
                </p>
                <a
                  href={`${isKlakklikkluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/register`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purpleSnail hover:text-whiteout transition-colors"
                >
                  {t('register.visitWebDashboard', 'Visit the web dashboard to register')}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Register
